import type { LeaderboardRow, LeaderboardSort } from '@ottd/api';
import { useLeaderboard, useServers } from '@ottd/api';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { AchievementIconRow } from '@/components/prototype/achievement-badge';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { PageHeader } from '@/components/prototype/page-header';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { PodiumCard } from '@/components/prototype/podium-card';
import { QueryState } from '@/components/prototype/query-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { formatNumber, formatSignedPoints } from '@/lib/format';
import { readPage, withPage } from '@/lib/page-param';
import { cn } from '@/lib/utils';
import { formatDateRange, PlayerLink } from '@/pages/shared';

const SORTS: LeaderboardSort[] = ['points', 'games', 'wins'];

/** Rows per ladder page (the API's page size; legacy used the same). */
const ROWS_PER_PAGE = 100;

/** The old site's page params, so inbound legacy links keep their page. */
const LEGACY_ALL_TIME_PAGE = 'page_players';
const LEGACY_SEASON_PAGE = 'page_points';

/** Medal strip sizing (px) shared by standings rows and podium cards. */
const MEDAL_SIZE = 32;
const MEDAL_ICON_SIZE = 13;

const SWITCH_LINK =
  'bevel bg-btn text-titletext font-display px-3 py-1.5 text-[11px] uppercase tracking-wide outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring';

function parsePositiveInt(value: string | null | undefined): number | undefined {
  const parsed = Number(value);
  return value !== null && value !== undefined && Number.isInteger(parsed) && parsed > 0
    ? parsed
    : undefined;
}

function parseSort(value: string | null): LeaderboardSort {
  return value !== null && (SORTS as string[]).includes(value)
    ? (value as LeaderboardSort)
    : 'points';
}

export default function LeaderboardPage() {
  const { t } = useTranslation('player');
  const { t: tc } = useTranslation('common');
  const [params, setParams] = useSearchParams();
  // /player/top is the all-time ladder; /season/:id (and the legacy
  // /season/:id/server/:server) scope it to one season. Only valid positive
  // integers are forwarded.
  const { id: seasonPath, server: serverPath } = useParams<{ id?: string; server?: string }>();
  const sort = parseSort(params.get('sort'));
  const season = parsePositiveInt(seasonPath);
  const server = parsePositiveInt(serverPath);
  const isAllTime = season === undefined;
  // Legacy per-ladder extras: career play time on /player/top, and how many
  // servers each player scored on across a whole season.
  const showsPlayTime = isAllTime;
  const showsServers = !isAllTime && server === undefined;
  const legacyPageKey = isAllTime ? LEGACY_ALL_TIME_PAGE : LEGACY_SEASON_PAGE;
  const page = readPage(params, legacyPageKey);

  const { data, isLoading, isError, error } = useLeaderboard({ season, server, sort, page });
  const rows = data?.data ?? [];
  const podium = data?.podium ?? [];
  const seasonFacts = data?.season ?? null;

  // Per-server standings exist only within a season page; offer the switcher there.
  const { data: servers } = useServers();
  const serverName = servers?.find((entry) => entry.id === server)?.name;

  // A new sort re-ranks the ladder, so it starts again from page 1.
  const setSort = (next: LeaderboardSort) => {
    const nextParams = withPage(params, 1, legacyPageKey);
    nextParams.set('sort', next);
    setParams(nextParams, { replace: true });
  };

  // Pushes a history entry, so the back button walks back through the pages.
  const setPage = (next: number) => {
    setParams(withPage(params, next, legacyPageKey));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const podiumBy = (rank: number) => podium.find((row) => row.rank === rank);

  return (
    <div className="mx-auto max-w-[1080px] px-4 py-6 sm:px-5">
      <PageHeader
        eyebrow={seasonEyebrow(isAllTime, season, serverName, t)}
        title={t('leaderboard.title', { top: page * ROWS_PER_PAGE })}
        subtitle={subtitleFor(isAllTime, server !== undefined, t)}
      />

      {/* The season's duration and games count (the server's own on a
          per-server board), as the legacy season pages showed above the list. */}
      {seasonFacts ? (
        <p className="text-dim mt-2 text-center font-mono text-xs">
          {formatDateRange(seasonFacts.startDate, seasonFacts.endDate)}
          {' · '}
          {tc('units.games', { count: seasonFacts.gamesCount })}
        </p>
      ) : null}

      {seasonPath && servers && servers.length > 0 ? (
        <nav aria-label={t('leaderboard.serverNavLabel')} className="mt-6 flex flex-wrap gap-1.5">
          <Link
            to={`/season/${seasonPath}`}
            className={cn(SWITCH_LINK, server === undefined && 'text-orange')}
          >
            {tc('allServers')}
          </Link>
          {servers.map((entry) => (
            <Link
              key={entry.id}
              to={`/season/${seasonPath}/server/${entry.id}`}
              className={cn(SWITCH_LINK, server === entry.id && 'text-orange')}
            >
              {entry.name}
            </Link>
          ))}
        </nav>
      ) : null}

      <QueryState
        className="mt-8"
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={rows.length === 0}
        emptyLabel={emptyLabelFor(page, server !== undefined, t)}
      >
        {podium.length > 0 ? (
          <div className="mb-8 grid items-end gap-4 sm:grid-cols-3">
            {[2, 1, 3].map((place) => (
              <LadderPodiumCard
                key={place}
                row={podiumBy(place)}
                place={place}
                isAllTime={isAllTime}
              />
            ))}
          </div>
        ) : null}

        <BevelWindow
          title={t('leaderboard.standings')}
          action={tc('units.ranked', { count: data?.meta.total ?? rows.length })}
          bodyClassName="p-0"
        >
          <Table aria-label={t('leaderboard.standingsLabel')}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pr-0">{t('leaderboard.columns.rank')}</TableHead>
                <TableHead>{t('leaderboard.columns.player')}</TableHead>
                <TableHead>{t('leaderboard.columns.level')}</TableHead>
                <TableHead>{t('leaderboard.columns.medals')}</TableHead>
                {showsServers ? <TableHead>{t('leaderboard.columns.servers')}</TableHead> : null}
                <SortableHead
                  label={t('leaderboard.columns.games')}
                  column="games"
                  sort={sort}
                  onSort={setSort}
                />
                <SortableHead
                  label={t('leaderboard.columns.wins')}
                  column="wins"
                  sort={sort}
                  onSort={setSort}
                />
                <TableHead>{t('leaderboard.columns.winPct')}</TableHead>
                {showsPlayTime ? <TableHead>{t('leaderboard.columns.playTime')}</TableHead> : null}
                <SortableHead
                  label={t('leaderboard.columns.score')}
                  column="points"
                  sort={sort}
                  onSort={setSort}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.playerId} className="even:bg-panel/40">
                  <TableCell className="text-faint w-10 pr-0">{row.rank}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <PlayerAvatar name={row.name} src={row.avatar} size={24} />
                      <PlayerLink name={row.name} className="text-strong font-sans" />
                    </span>
                  </TableCell>
                  <TableCell className="text-dim font-display text-[11px]">
                    {tc('units.level', { level: row.level })}
                  </TableCell>
                  <TableCell>
                    <AchievementIconRow
                      badges={row.achievements}
                      size={MEDAL_SIZE}
                      iconSize={MEDAL_ICON_SIZE}
                    />
                  </TableCell>
                  {showsServers ? <TableCell>{formatNumber(row.servers)}</TableCell> : null}
                  <TableCell>{formatNumber(row.games)}</TableCell>
                  <TableCell>{formatNumber(row.wins)}</TableCell>
                  <TableCell className="text-faint">{row.winPct}%</TableCell>
                  {showsPlayTime ? (
                    <TableCell className="text-faint whitespace-nowrap">
                      {row.playTimeHuman ?? '-'}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-green font-display text-[11px]">
                    {formatSignedPoints(row.points)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </BevelWindow>
      </QueryState>

      {/* Outside the QueryState so a page past the end still offers a way back. */}
      {data ? (
        <Pagination
          className="mt-6"
          page={page}
          lastPage={data.meta.last_page}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

function SortableHead({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: LeaderboardSort;
  sort: LeaderboardSort;
  onSort: (sort: LeaderboardSort) => void;
}) {
  const active = sort === column;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-pressed={active}
        className={cn(
          'font-display inline-flex items-center gap-1 text-[10px] uppercase tracking-wide outline-none hover:brightness-118 focus-visible:ring-2 focus-visible:ring-ring',
          active ? 'text-orange' : 'text-titletext',
        )}
      >
        {label}
        {active ? <span aria-hidden>▼</span> : null}
      </button>
    </TableHead>
  );
}

/** "All time" or the season (optionally narrowed to one server) being shown. */
function seasonEyebrow(
  isAllTime: boolean,
  season: number | undefined,
  serverName: string | undefined,
  t: TFunction<'player'>,
): string {
  if (isAllTime) return t('leaderboard.allTime');
  return serverName
    ? t('leaderboard.seasonEyebrowServer', { num: season, server: serverName })
    : t('leaderboard.seasonEyebrow', { num: season });
}

/** A page past the end is empty for a different reason than an empty season. */
function emptyLabelFor(page: number, isServerScoped: boolean, t: TFunction<'player'>): string {
  if (page > 1) return t('leaderboard.emptyPage');
  return isServerScoped ? t('leaderboard.emptyServer') : t('leaderboard.emptySeason');
}

function subtitleFor(isAllTime: boolean, isServerScoped: boolean, t: TFunction<'player'>): string {
  if (isAllTime) return t('leaderboard.subtitleAllTime');
  return isServerScoped ? t('leaderboard.subtitleServer') : t('leaderboard.subtitleSeason');
}

function LadderPodiumCard({
  row,
  place,
  isAllTime,
}: {
  row: LeaderboardRow | undefined;
  place: number;
  isAllTime: boolean;
}) {
  const { t } = useTranslation('player');

  return (
    <PodiumCard
      row={row}
      place={place}
      leaderTitle={isAllTime ? t('leaderboard.leaderAllTime') : t('leaderboard.leaderSeason')}
      caption={isAllTime ? t('leaderboard.captionAllTime') : t('leaderboard.captionSeason')}
      badges={
        row ? (
          <AchievementIconRow
            badges={row.achievements}
            size={MEDAL_SIZE}
            iconSize={MEDAL_ICON_SIZE}
          />
        ) : null
      }
    />
  );
}
